import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, FileText, CheckCircle, ArrowRight, Scale, Home, GraduationCap, Heart, Wrench } from 'lucide-react';
import SEO, { SEOPages } from '../components/SEO';

const Fiscalite = () => {
  const fiscalServices = [
    {
      icon: Calculator,
      title: "Optimisation fiscale",
      description: "Réduisez légalement vos impôts grâce à nos stratégies personnalisées",
      benefits: ["Déductions maximisées", "Planification pluriannuelle", "Conseil en temps réel"]
    },
    {
      icon: FileText,
      title: "Déclaration d'impôts",
      description: "Service complet de préparation et dépôt de vos déclarations",
      benefits: ["Expertise Suisse romande", "Contrôle qualité", "Suivi personnalisé"]
    }
  ];

  const deductionTypes = [
    { 
      name: "3e pilier", 
      icon: GraduationCap,
      desc: "Prévoyance privée liée" 
    },
    { 
      name: "Rachats LPP", 
      icon: Calculator,
      desc: "Selon lacunes de prévoyance" 
    },
    { 
      name: "Frais professionnels", 
      icon: Scale,
      desc: "Déplacement, formation, matériel" 
    },
    { 
      name: "Déduction des intérêts et dettes", 
      icon: Home,
      desc: "Optimisation de votre situation fiscale selon les règles en vigueur." 
    },
    { 
      name: "Assurance vie", 
      icon: Heart,
      desc: "Primes d'assurance vie" 
    },
    { 
      name: "Rénovations", 
      icon: Wrench,
      desc: "Travaux d'amélioration énergétique" 
    }
  ];

  return (
    <div className="pt-20">
      <SEO {...SEOPages.fiscalite} />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Optimisation fiscale Suisse - déductions 3ème pilier et planification fiscale"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1540762693098-50320eb8a752?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdHxlbnwwfHx8Ymx1ZXwxNzYxODUwMDI4fDA&ixlib=rb-4.1.0&q=85)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              Fiscalité : <span className="text-green-300">optimisez</span> vos impôts
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Réduisez efficacement votre charge fiscale grâce à une stratégie personnalisée et à notre expertise.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Analyser ma déclaration d'impôts
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-8 py-4 flex items-center justify-center">
                <Scale className="mr-2" size={20} />
                Obtenir un conseil fiscal
              </Link>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-green-400 mb-2">Économies</div>
                <div className="text-gray-300">Réduction fiscale optimisée</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-blue-400 mb-2">Expertise</div>
                <div className="text-gray-300">Conseil fiscal personnalisé</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-white mb-2">Confiance</div>
                <div className="text-gray-300">Approche transparente et éthique</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services fiscaux */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos services fiscaux
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Une approche complète pour optimiser votre situation fiscale
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {fiscalServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="glass p-8 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-300">
                        <CheckCircle size={16} className="text-green-400 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Déductions possibles */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Exemples de déductions avec notre accompagnement
            </h2>
            <p className="text-xl text-gray-300">
              Maximisez vos déductions avec notre accompagnement expert
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deductionTypes.map((deduction, index) => {
              const IconComponent = deduction.icon;
              return (
                <div key={index} className="glass p-6 rounded-2xl group hover:scale-105 transition-transform">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center mr-4">
                      <IconComponent size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">{deduction.name}</h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">{deduction.desc}</p>
                  <div className="mt-4 h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full opacity-60"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prêt à alléger vos impôts ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Nous vous aidons à identifier les solutions simples et légales qui peuvent réduire vos charges fiscales.
            </p>
            <div className="flex justify-center">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4">
                Demander un conseil
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Fiscalite;